"use client";

import { create } from "zustand";
import { ShopProduct, ProductPagination } from "@/lib/types/shop";

interface ShopState {
  // Keyed by brandSlug or "marketplace" for general shop
  productsByShop: Record<string, {
    products: ShopProduct[];
    page: number;
    pagination: ProductPagination;
    scrollPosition: number;
  }>;
  
  // Actions
  setShopProducts: (
    shopKey: string,
    products: ShopProduct[],
    page: number,
    pagination: ProductPagination
  ) => void;
  appendProducts: (
    shopKey: string,
    newProducts: ShopProduct[],
    page: number,
    pagination: ProductPagination
  ) => void;
  setScrollPosition: (shopKey: string, position: number) => void;
  getShopState: (shopKey: string) => {
    products: ShopProduct[];
    page: number;
    pagination: ProductPagination;
    scrollPosition: number;
  } | null;
  clearShop: (shopKey: string) => void;
}

export const useShopStore = create<ShopState>((set, get) => ({
  productsByShop: {},

  setShopProducts: (shopKey, products, page, pagination) => {
    set((state) => ({
      productsByShop: {
        ...state.productsByShop,
        [shopKey]: {
          products,
          page,
          pagination,
          scrollPosition: state.productsByShop[shopKey]?.scrollPosition || 0,
        },
      },
    }));
  },

  appendProducts: (shopKey, newProducts, page, pagination) => {
    set((state) => {
      const existing = state.productsByShop[shopKey];
      return {
        productsByShop: {
          ...state.productsByShop,
          [shopKey]: {
            products: existing ? [...existing.products, ...newProducts] : newProducts,
            page,
            pagination,
            scrollPosition: existing?.scrollPosition || 0,
          },
        },
      };
    });
  },

  setScrollPosition: (shopKey, position) => {
    set((state) => {
      const existing = state.productsByShop[shopKey];
      if (!existing) return state;
      return {
        productsByShop: {
          ...state.productsByShop,
          [shopKey]: {
            ...existing,
            scrollPosition: position,
          },
        },
      };
    });
  },

  getShopState: (shopKey) => {
    return get().productsByShop[shopKey] || null;
  },

  clearShop: (shopKey) => {
    set((state) => {
      const { [shopKey]: _, ...rest } = state.productsByShop;
      return { productsByShop: rest };
    });
  },
}));
